import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Extension, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import { RequirePermission } from '../contexts/PermissionContext';
import { useNumberFormat } from '../hooks/useNumberFormat';
import usePageTitle from '../hooks/usePageTitle';
import { deleteAddonAdmin, fetchAddonsAdmin } from '../services/subscriptions';
import type { Addon } from '../types';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';

export default function AddonsPage() {
  const { t } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:addons.title'));
  const { formatCurrency } = useNumberFormat();
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: 'name',
  });

  const searchValue = filters.search ?? '';
  const orderingParam = ordering || 'name';

  const loadAddons = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAddonsAdmin();
      setAddons(data);
    } catch (error) {
      console.error('Error loading addons:', error);
      setAddons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  const handleDelete = async (addonId: string) => {
    if (!confirm(t('pages:addons.deleteConfirm'))) {
      return;
    }
    try {
      await deleteAddonAdmin(addonId);
      loadAddons();
    } catch (error) {
      console.error('Error deleting addon:', error);
    }
  };

  const headerActions = (
    <>
      <ListActionButton variant="outlined" startIcon={<Refresh />} onClick={loadAddons}>
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="subscriptions_addons_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/subscriptions/addons/new"
        >
          {t('pages:addons.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  const filteredAddons = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const list = normalizedSearch
      ? addons.filter((addon) =>
          `${addon.name} ${addon.description ?? ''}`.toLowerCase().includes(normalizedSearch)
        )
      : addons;

    return [...list].sort((a, b) => {
      if (orderingParam === '-name') {
        return b.name.localeCompare(a.name);
      }
      return a.name.localeCompare(b.name);
    });
  }, [addons, orderingParam, searchValue]);

  const paginatedAddons = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAddons.slice(start, start + rowsPerPage);
  }, [filteredAddons, page, rowsPerPage]);

  const filterControls = (
    <TextField
      size="small"
      label={t('common:actions.search')}
      placeholder={t('pages:addons.filters.searchPlaceholder')}
      value={searchValue}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setParams({ search: value || undefined }, { resetPage: true });
      }}
      sx={{ minWidth: 240, flex: '1 1 240px' }}
    />
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t('pages:addons.columns.name')}</TableCell>
          <TableCell>{t('pages:addons.columns.code')}</TableCell>
          <TableCell>{t('pages:addons.columns.price')}</TableCell>
          <TableCell>{t('pages:addons.columns.status')}</TableCell>
          <TableCell align="right">{t('common:actions.actions')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography color="text.secondary">{t('common:common.loading')}</Typography>
            </TableCell>
          </TableRow>
        ) : filteredAddons.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography color="text.secondary">{t('common:common.noData')}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          paginatedAddons.map((addon) => (
            <TableRow key={addon.id} hover>
              <LinkTableCell to={`/subscriptions/addons/${addon.id}`}>
                {addon.name}
              </LinkTableCell>
              <TableCell>{t(`pages:addons.codes.${addon.code}`)}</TableCell>
              <TableCell>{formatCurrency(Number(addon.priceAmount), addon.priceCurrency, 2)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={addon.isActive ? 'success' : 'default'}
                  label={addon.isActive ? t('common:status.active') : t('common:status.inactive')}
                />
              </TableCell>
              <TableCell align="right">
                <RequirePermission permission="subscriptions_addons_update">
                  <IconButton component={RouterLink} to={`/subscriptions/addons/${addon.id}`} size="small">
                    <Edit fontSize="small" />
                  </IconButton>
                </RequirePermission>
                <RequirePermission permission="subscriptions_addons_delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(addon.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </RequirePermission>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <FilteredListLayout
      headerProps={{
        icon: <Extension />,
        title: t('pages:addons.title'),
        subtitle: t('pages:addons.subtitle'),
        actions: headerActions,
      }}
      filterControls={filterControls}
      table={table}
      tableFooter={(
        <LinkPagination
          count={filteredAddons.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
          getPageLink={getPageLink}
        />
      )}
    />
  );
}
